import {
  Select,
  Table,
  TableBody,
  TableHeader,
  TableHeading,
  TableRow,
  useFirstRender,
  SearchActionBar,
} from '@insurancemenu/imenu-components-lib';
import clsx from 'clsx';
import DateRange from 'components/common/general/DateRange';
import SearchBlock, { SearchInfo } from 'components/common/general/SearchBlock';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useRootStore } from 'store/rootStore';
import { GeneralStatus } from 'types/emailService';
import EmailContextModal from './EmailContextModal';
import EmailsTableRow from './EmailsTableRow';
import { createStyles } from './styles';

const EmailsDashboard = observer((): JSX.Element => {
  const classes = createStyles();
  const firstRender = useFirstRender();
  const {
    emailsDashboardStore: {
      emailType,
      emailStatus,
      sponsorId,
      totalElements,
      totalPages,
      page,
      searchEmail,
      date: { from: dateFrom, to: dateTo },
      loading,
      emails,
      filteredEmailTypes,
      getSponsorOptionsWithOptionAll,
      getEmailStatusOptions,
      getEmailTypeOptions,
      setFilteredEmailTypes,
      setSearchEmailAddress,
      updateDate,
      setSponsorId,
      setEmailType,
      setEmailStatus,
      getInitialData,
      getEmails,
      setPage,
      searchTypes,
    },
  } = useRootStore();

  const convertedEmails = emails.map((email) => toJS(email)).slice();
  const convertedFilteredEmailTypes = filteredEmailTypes.map((type) => toJS(type)).slice();

  const resetSearchValue = (): void => {
    setFilteredEmailTypes([]);
  };

  useEffect(() => {
    void getInitialData();
  }, []);

  useEffect(() => {
    void getEmails();
    resetSearchValue();
  }, [page]);

  useEffect(() => {
    if (!firstRender) {
      setPage(1);
      resetSearchValue();
      void getEmails();
    }
  }, [sponsorId, emailType, emailStatus, dateFrom, dateTo, searchEmail]);

  const emailsList = convertedEmails.map((email) => <EmailsTableRow key={email.id} email={email} />);

  const selectEmailTypeFromSearch = (type: string): void => {
    setEmailType(type);
    resetSearchValue();
  };

  return (
    <div className={classes.content}>
      <div className={classes.actionHeader}>
        <div className={classes.leftFilters}>
          <div className={classes.dateRange}>
            <DateRange updateTableByDateRange={updateDate} showAdminToggle={false} />
          </div>
          <Select
            label="Sponsor Id"
            name="sponsorId"
            options={getSponsorOptionsWithOptionAll()}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              setSponsorId(e.target.value);
            }}
            value={sponsorId}
            width="23rem"
          />
          <Select
            label="Email Status"
            name="emailStatus"
            options={getEmailStatusOptions()}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              setEmailStatus(e.target.value ? (e.target.value as GeneralStatus) : null);
            }}
            value={emailStatus || ''}
            width="23rem"
          />
        </div>
        <div className={classes.filters}>
          <div className={classes.rightFilters}>
            <Select
              label="Email Type"
              name="emailType"
              options={getEmailTypeOptions()}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                setEmailType(e.target.value);
              }}
              value={emailType}
              width="25rem"
            />
            <SearchBlock
              inputLabel="Type to find an email type"
              placeholder="Search Email type"
              filteredInfo={convertedFilteredEmailTypes.map((type) => ({ name: type, id: type }))}
              resetFilteredItems={resetSearchValue}
              searchByValue={searchTypes}
              selectFromSearch={(info: SearchInfo): void => selectEmailTypeFromSearch(info.id)}
            />
          </div>
        </div>
      </div>
      <div className={classes.tableWrapper}>
        <SearchActionBar
          totalElements={totalElements}
          totalTitle="Total Emails"
          searchPlaceholder="Email Address"
          isSearchButtonDisabled={loading}
          onSearch={setSearchEmailAddress}
          onReset={(): void => {
            setSearchEmailAddress('');
          }}
        />
        <Table
          className={classes.table}
          loading={loading}
          maxHeight="55rem"
          minHeight="55rem"
          showScrollShadows
          showRowHover
          totalElements={totalElements}
          pagination={{
            onPageChange: setPage,
            maxPage: totalPages || 1,
            tablePage: page,
          }}
        >
          <TableHeader className={classes.tableHeader}>
            <TableRow className={classes.headingsRow}>
              <TableHeading className={classes.tableHeading}>Sponsor Id</TableHeading>
              <TableHeading className={classes.tableHeading}>Email Type</TableHeading>
              <TableHeading className={classes.tableHeading}>Recipient email</TableHeading>
              <TableHeading className={classes.tableHeading}>Recipient roles</TableHeading>
              <TableHeading className={clsx(classes.tableHeading, classes.emailStatusHeading)}>
                Email Status
              </TableHeading>
              <TableHeading className={classes.tableHeading}>Creation date time</TableHeading>
              <TableHeading className={classes.tableHeading} />
            </TableRow>
          </TableHeader>
          <TableBody className={classes.tableBody}>{emailsList}</TableBody>
        </Table>
      </div>
      <EmailContextModal />
    </div>
  );
});

export default EmailsDashboard;
